import { adminReferenceLabel } from '../components/AdminReference';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import AdminPagination from '../components/AdminPagination';
import {
  confirmAdminBooking,
  createAdminBookingPaymentLink,
  fetchAdminBooking,
  fetchAdminBookingBasket,
  fetchAdminBookingBaskets,
  fetchAdminBookings,
  fetchAdminConsultantRequests,
  fetchAdminGuestBookingLeads,
  markAdminBookingPaid,
  markAdminBookingPaymentFailed,
  markAdminBookingPaymentPending,
  rejectAdminBooking,
  updateAdminGuestBookingLead,
} from '../services/booking';

const BOOKING_STATUS_OPTIONS = [
  { value: '', label: 'All booking statuses' },
  { value: 'tutor_selected', label: 'Tutor Selected' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'pending_admin_confirmation', label: 'Awaiting Tutor Response' },
  { value: 'tutor_response_overdue', label: 'Tutor No Response' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'rejected', label: 'Rejected' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All payment statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'checkout_created', label: 'Gateway Checkout Created' },
  { value: 'checkout_pending', label: 'Gateway Checkout Pending' },
  { value: 'submitted_manual', label: 'Submitted Manual' },
  { value: 'paid_manual', label: 'Paid Manual' },
  { value: 'paid_gateway', label: 'Paid By Gateway' },
  { value: 'paid_mock', label: 'Paid Mock' },
  { value: 'uncertain', label: 'Uncertain' },
  { value: 'failed', label: 'Failed' },
];

const DELIVERY_MODE_OPTIONS = [
  { value: '', label: 'All delivery modes' },
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-Person' },
];

const CURRICULUM_OPTIONS = [
  { value: '', label: 'All curricula' },
  { value: 'CAPS', label: 'CAPS' },
  { value: 'IEB', label: 'IEB' },
  { value: 'CAMBRIDGE', label: 'Cambridge' },
  { value: 'IB', label: 'IB' },
  { value: 'PEARSON EDEXCEL', label: 'Pearson Edexcel' },
];

const PROVINCE_OPTIONS = [
  { value: '', label: 'All provinces' },
  { value: 'Gauteng', label: 'Gauteng' },
  { value: 'Western Cape', label: 'Western Cape' },
  { value: 'KwaZulu-Natal', label: 'KwaZulu-Natal' },
  { value: 'Eastern Cape', label: 'Eastern Cape' },
  { value: 'Free State', label: 'Free State' },
  { value: 'Limpopo', label: 'Limpopo' },
  { value: 'Mpumalanga', label: 'Mpumalanga' },
  { value: 'North West', label: 'North West' },
  { value: 'Northern Cape', label: 'Northern Cape' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const formatAdminMoney = (amount, currency = 'ZAR') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '-';
  const code = String(currency || 'ZAR').toUpperCase();
  if (code === 'ZAR') return `R${Math.round(numeric).toLocaleString('en-ZA')}`;
  if (code === 'USD') return `$${numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${code} ${numeric.toLocaleString('en-ZA')}`;
};

const formatPerson = (user) => {
  if (!user) return '-';
  const full = [user.name, user.surname].filter(Boolean).join(' ').trim();
  return full || user.email || '-';
};

const bookingTutorAssignments = (booking = {}) => {
  const allocations = Array.isArray(booking.subjectAllocations) ? booking.subjectAllocations : [];
  const assignedAllocations = allocations
    .map((allocation) => ({
      subject: allocation.subject || 'Subject',
      tutor: allocation.selectedTutor || allocation.selectedTutorId || null,
    }))
    .filter(({ tutor }) => formatPerson(tutor) !== '-');

  if (assignedAllocations.length) return assignedAllocations;
  const tutor = booking.selectedTutor || booking.selectedTutorId || null;
  return formatPerson(tutor) === '-' ? [] : [{ subject: null, tutor }];
};

const formatGradeLabel = (grade) => {
  if (grade === 'R') return 'Grade R';
  const numeric = Number(grade);
  if (numeric === 0) return 'Grade R';
  if (Number.isFinite(numeric) && numeric > 0) return `Grade ${numeric}`;
  if (String(grade || '').trim()) return `Grade ${grade}`;
  return '-';
};

const formatDeliveryMode = (value) =>
  String(value || '-')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatList = (value) => {
  if (!Array.isArray(value)) return '-';
  const items = value.filter(Boolean);
  return items.length ? items.join(', ') : '-';
};

const formatAvailability = (availability) => {
  if (!availability || typeof availability !== 'object') return 'Not set';
  const days = Array.isArray(availability.daysAvailable) ? availability.daysAvailable.filter(Boolean) : [];
  const slots = Array.isArray(availability.timeSlots) ? availability.timeSlots.filter(Boolean) : [];
  const labels = [];
  if (days.length) labels.push(days.join(', '));
  if (slots.length) labels.push(slots.join(', '));
  if (availability.timeZone) labels.push(availability.timeZone);
  if (availability.hasWeekendAvailability) labels.push('Weekend support');
  if (availability.hasEmergencyExamSupport) labels.push('Emergency exam support');
  return labels.length ? labels.join(' · ') : 'Not set';
};

const statusLabel = (value) => {
  switch (String(value || '').toLowerCase()) {
    case 'payment_pending':
      return 'Payment Pending';
    case 'tutor_selected':
      return 'Tutor Selected';
    case 'pending_admin_confirmation':
      return 'Awaiting Tutor Response';
    case 'tutor_response_overdue':
      return 'Tutor No Response';
    case 'submitted_manual':
      return 'Manual Payment Submitted';
    case 'checkout_created':
      return 'Gateway Checkout Created';
    case 'checkout_pending':
      return 'Gateway Checkout Pending';
    case 'paid_manual':
      return 'Paid Manually';
    case 'paid_gateway':
      return 'Paid By Gateway';
    case 'paid_mock':
      return 'Paid (Mock)';
    case 'refunded_gateway':
      return 'Refunded By Gateway';
    case 'not_started':
      return 'Not Started';
    default:
      return String(value || '-')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
  }
};

const timelineLabel = (entry) => entry?.label || statusLabel(entry?.type);

const statusColor = (value) => {
  switch (String(value || '').toLowerCase()) {
    case 'confirmed':
    case 'paid_manual':
    case 'paid_gateway':
    case 'paid_mock':
    case 'accepted':
      return 'success';
    case 'rejected':
    case 'failed':
      return 'error';
    case 'pending_admin_confirmation':
    case 'tutor_response_overdue':
    case 'checkout_created':
    case 'checkout_pending':
    case 'submitted_manual':
    case 'uncertain':
      return 'warning';
    default:
      return 'default';
  }
};

const timelineTone = (value) => {
  switch (value) {
    case 'success':
      return 'success.main';
    case 'error':
      return 'error.main';
    case 'warning':
      return 'warning.main';
    default:
      return 'text.secondary';
  }
};

const PAID_PAYMENT_STATUSES = ['paid_manual', 'paid_gateway', 'paid_mock'];
const INACTIVE_PAYMENT_LINK_BOOKING_STATUSES = ['confirmed', 'rejected', 'cancelled'];

const getBookingCurrency = (booking) =>
  String(booking?.billingCurrency || booking?.pricingSnapshot?.currency || '').toUpperCase();

const defaultPaymentProvider = (booking) => (getBookingCurrency(booking) === 'ZAR' ? 'yoco' : 'paypal');

const paymentLinkForPayment = (payment) =>
  payment?.link ||
  payment?.payload?.paypalApprovalUrl ||
  payment?.payload?.yocoRedirectUrl ||
  '';

const defaultPaymentAmount = (booking, provider) => {
  const targetCurrency = provider === 'paypal' ? 'USD' : 'ZAR';
  if (getBookingCurrency(booking) !== targetCurrency) return '';
  const value = Number(
    booking?.pricingSnapshot?.totalClientAmount ??
    booking?.pricingSnapshot?.clientRate ??
    0
  );
  return Number.isFinite(value) && value > 0 ? value.toFixed(2) : '';
};

const canCreatePaymentLink = (booking) => {
  if (!booking?._id) return false;
  if (INACTIVE_PAYMENT_LINK_BOOKING_STATUSES.includes(String(booking.bookingStatus))) return false;
  if (PAID_PAYMENT_STATUSES.includes(String(booking.paymentStatus))) return false;
  return true;
};

const Bookings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookingStatus, setBookingStatus] = useState('pending_admin_confirmation');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [curriculum, setCurriculum] = useState('');
  const [province, setProvince] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [bookings, setBookings] = useState([]);
  const [bookingBaskets, setBookingBaskets] = useState([]);
  const [bookingBasketsLoading, setBookingBasketsLoading] = useState(false);
  const [basketDetail, setBasketDetail] = useState(null);
  const [basketDetailLoading, setBasketDetailLoading] = useState(false);
  const [guestLeads, setGuestLeads] = useState([]);
  const [guestSearchInput, setGuestSearchInput] = useState('');
  const [guestQuery, setGuestQuery] = useState('');
  const [guestStep, setGuestStep] = useState('');
  const [guestFollowUp, setGuestFollowUp] = useState('');
  const [selectedGuestLead, setSelectedGuestLead] = useState(null);
  const [consultantRequests, setConsultantRequests] = useState([]);
  const [guestLeadLoading, setGuestLeadLoading] = useState(false);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', booking: null });
  const [actionNote, setActionNote] = useState('');
  const [actionReference, setActionReference] = useState('');
  const [actionMethod, setActionMethod] = useState('manual_transfer');
  const [actionEvidenceSource, setActionEvidenceSource] = useState('bank_reference');
  const [submitting, setSubmitting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);
  const [tutorProfileDialog, setTutorProfileDialog] = useState({ open: false, tutor: null, context: '' });
  const [paymentLinkDialog, setPaymentLinkDialog] = useState({ open: false, booking: null });
  const [paymentLinkProvider, setPaymentLinkProvider] = useState('yoco');
  const [paymentLinkAmount, setPaymentLinkAmount] = useState('');
  const [paymentLinkNote, setPaymentLinkNote] = useState('');
  const [paymentLinkSubmitting, setPaymentLinkSubmitting] = useState(false);
  const [paymentLinkError, setPaymentLinkError] = useState('');
  const [latestPaymentLink, setLatestPaymentLink] = useState('');
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, booking: null });
  const initialPageSize = (key) => [25, 50, 100].includes(Number(searchParams.get(key))) ? Number(searchParams.get(key)) : 25;
  const initialPage = (key) => Math.max(1, Number(searchParams.get(key)) || 1);
  const [bookingPage, setBookingPage] = useState(() => initialPage('bookingsPage'));
  const [bookingPageSize, setBookingPageSize] = useState(() => initialPageSize('bookingsPageSize'));
  const [bookingPagination, setBookingPagination] = useState({ page: bookingPage, pages: 1, total: 0, limit: bookingPageSize });
  const [guestPage, setGuestPage] = useState(() => initialPage('guestPage'));
  const [guestPageSize, setGuestPageSize] = useState(() => initialPageSize('guestPageSize'));
  const [guestPagination, setGuestPagination] = useState({ page: guestPage, pages: 1, total: 0, limit: guestPageSize });
  const [consultantPage, setConsultantPage] = useState(() => initialPage('consultantPage'));
  const [consultantPageSize, setConsultantPageSize] = useState(() => initialPageSize('consultantPageSize'));
  const [consultantPagination, setConsultantPagination] = useState({ page: consultantPage, pages: 1, total: 0, limit: consultantPageSize });
  const bookingRequestSequence = useRef(0);
  const guestRequestSequence = useRef(0);
  const consultantRequestSequence = useRef(0);

  const updatePaginationUrl = useCallback((pageKey, sizeKey, nextPage, nextPageSize) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set(pageKey, String(nextPage));
      next.set(sizeKey, String(nextPageSize));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const changeBookingPage = useCallback((nextPage, nextSize = bookingPageSize) => {
    setBookingPage(nextPage); setBookingPageSize(nextSize);
    updatePaginationUrl('bookingsPage', 'bookingsPageSize', nextPage, nextSize);
  }, [bookingPageSize, updatePaginationUrl]);
  const changeGuestPage = useCallback((nextPage, nextSize = guestPageSize) => {
    setGuestPage(nextPage); setGuestPageSize(nextSize);
    updatePaginationUrl('guestPage', 'guestPageSize', nextPage, nextSize);
  }, [guestPageSize, updatePaginationUrl]);
  const changeConsultantPage = useCallback((nextPage, nextSize = consultantPageSize) => {
    setConsultantPage(nextPage); setConsultantPageSize(nextSize);
    updatePaginationUrl('consultantPage', 'consultantPageSize', nextPage, nextSize);
  }, [consultantPageSize, updatePaginationUrl]);

  const loadBookings = useCallback(async () => {
    const sequence = ++bookingRequestSequence.current;
    try {
      setLoading(true);
      setError('');
      const data = await fetchAdminBookings({
        bookingStatus: bookingStatus || undefined,
        paymentStatus: paymentStatus || undefined,
        deliveryMode: deliveryMode || undefined,
        curriculum: curriculum || undefined,
        province: province || undefined,
        q: query || undefined,
        page: bookingPage,
        limit: bookingPageSize,
      });
      if (sequence !== bookingRequestSequence.current) return;
      setBookings(Array.isArray(data?.items) ? data.items : []);
      setSummaryData(data?.summary || null);
      const nextPagination = { page: Number(data?.page) || 1, pages: Number(data?.pages) || 1, total: Number(data?.total) || 0, limit: Number(data?.limit) || bookingPageSize };
      setBookingPagination(nextPagination);
      if (nextPagination.page !== bookingPage) changeBookingPage(nextPagination.page, nextPagination.limit);
    } catch (err) {
      if (sequence !== bookingRequestSequence.current) return;
      setBookings([]);
      setError(err?.message || 'Failed to load bookings');
    } finally {
      if (sequence === bookingRequestSequence.current) setLoading(false);
    }
  }, [bookingPage, bookingPageSize, bookingStatus, changeBookingPage, curriculum, deliveryMode, paymentStatus, province, query]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const loadBookingBaskets = useCallback(async () => {
    try {
      setBookingBasketsLoading(true);
      const data = await fetchAdminBookingBaskets({ limit: 25 });
      setBookingBaskets(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      // The feature is flag-gated in the API; a disabled feature should not
      // make the existing bookings screen fail to load.
      if (err?.response?.status !== 404) setError(err?.message || 'Failed to load family bookings');
      setBookingBaskets([]);
    } finally {
      setBookingBasketsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookingBaskets();
  }, [loadBookingBaskets]);

  const openBasketDetail = useCallback(async (basket) => {
    if (!basket?._id) return;
    try {
      setBasketDetailLoading(true);
      setBasketDetail(await fetchAdminBookingBasket(basket._id));
    } catch (err) {
      setError(err?.message || 'Failed to load family booking');
    } finally {
      setBasketDetailLoading(false);
    }
  }, []);

  const loadGuestLeads = useCallback(async () => {
    const sequence = ++guestRequestSequence.current;
    try {
      setGuestLeadLoading(true);
      const data = await fetchAdminGuestBookingLeads({ page: guestPage, limit: guestPageSize, q: guestQuery || undefined, step: guestStep || undefined, followUp: guestFollowUp || undefined });
      if (sequence !== guestRequestSequence.current) return;
      setGuestLeads(Array.isArray(data?.items) ? data.items : []);
      const nextPagination = { page: Number(data?.page) || 1, pages: Number(data?.pages) || 1, total: Number(data?.total) || 0, limit: Number(data?.limit) || guestPageSize };
      setGuestPagination(nextPagination);
      if (nextPagination.page !== guestPage) changeGuestPage(nextPagination.page, nextPagination.limit);
    } catch (err) {
      if (sequence !== guestRequestSequence.current) return;
      setError(err?.message || 'Failed to load abandoned guest bookings');
    } finally {
      if (sequence === guestRequestSequence.current) setGuestLeadLoading(false);
    }
  }, [changeGuestPage, guestPage, guestPageSize, guestQuery, guestStep, guestFollowUp]);

  useEffect(() => {
    loadGuestLeads();
  }, [loadGuestLeads]);

  const loadConsultantRequests = useCallback(async () => {
    const sequence = ++consultantRequestSequence.current;
    try {
      setConsultantLoading(true);
      const data = await fetchAdminConsultantRequests({ page: consultantPage, limit: consultantPageSize });
      if (sequence !== consultantRequestSequence.current) return;
      setConsultantRequests(Array.isArray(data?.items) ? data.items : []);
      const nextPagination = { page: Number(data?.page) || 1, pages: Number(data?.pages) || 1, total: Number(data?.total) || 0, limit: Number(data?.limit) || consultantPageSize };
      setConsultantPagination(nextPagination);
      if (nextPagination.page !== consultantPage) changeConsultantPage(nextPagination.page, nextPagination.limit);
    } catch (err) {
      if (sequence !== consultantRequestSequence.current) return;
      setError(err?.message || 'Failed to load manual tutor matching requests');
    } finally {
      if (sequence === consultantRequestSequence.current) setConsultantLoading(false);
    }
  }, [changeConsultantPage, consultantPage, consultantPageSize]);

  useEffect(() => {
    loadConsultantRequests();
  }, [loadConsultantRequests]);

  const handleGuestLeadAction = async (leadId, action) => {
    try {
      await updateAdminGuestBookingLead(leadId, action);
      await loadGuestLeads();
    } catch (err) {
      setError(err?.message || 'Failed to update abandoned guest booking');
    }
  };

  const summary = useMemo(() => {
    if (summaryData?.totals) {
      return {
        awaiting: Number(summaryData.totals.awaitingAdmin || 0),
        confirmed: Number(summaryData.totals.confirmed || 0),
        rejected: Number(summaryData.totals.rejected || 0),
        paid: Number(summaryData.totals.paid || 0),
      };
    }
    return bookings.reduce(
      (acc, booking) => {
        const status = String(booking?.bookingStatus || '');
        const payment = String(booking?.paymentStatus || '');
        if (status === 'pending_admin_confirmation') acc.awaiting += 1;
        if (status === 'confirmed') acc.confirmed += 1;
        if (status === 'rejected') acc.rejected += 1;
        if (payment === 'paid_mock') acc.paid += 1;
        if (payment === 'paid_manual') acc.paid += 1;
        if (payment === 'paid_gateway') acc.paid += 1;
        return acc;
      },
      { awaiting: 0, confirmed: 0, rejected: 0, paid: 0 }
    );
  }, [bookings, summaryData]);

  const openAction = (type, booking) => {
    setActionDialog({ open: true, type, booking });
    setActionNote('');
    setActionReference('');
    setActionMethod('manual_transfer');
    setActionEvidenceSource('bank_reference');
  };

  const closeAction = () => {
    if (submitting) return;
    setActionDialog({ open: false, type: '', booking: null });
    setActionNote('');
    setActionReference('');
    setActionMethod('manual_transfer');
    setActionEvidenceSource('bank_reference');
  };

  const openDetail = async (booking) => {
    if (!booking?._id) return;
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setError('');
      const data = await fetchAdminBooking(booking._id);
      setDetailBooking(data);
    } catch (err) {
      setError(err?.message || 'Failed to load booking detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailBooking(null);
    setDetailLoading(false);
  };

  const tutorIdOf = (tutor) => String(tutor?._id || tutor?.id || tutor || '');

  const openTutorProfile = (tutor, context = '') => {
    if (!tutorIdOf(tutor)) return;
    setTutorProfileDialog({ open: true, tutor: typeof tutor === 'object' ? tutor : { _id: tutor }, context });
  };

  const messageTutor = (tutor, booking = detailBooking, subject = '') => {
    const tutorId = tutorIdOf(tutor);
    if (!tutorId) return;
    const params = new URLSearchParams({ contactId: tutorId, contactKind: 'lms_user' });
    if (booking?.bookingNumber) params.set('bookingNumber', booking.bookingNumber);
    if (subject || booking?.subject) params.set('subject', subject || booking.subject);
    navigate(`/admin/messages?${params.toString()}`);
  };

  const openActionMenu = (event, booking) => {
    setActionMenu({ anchorEl: event.currentTarget, booking });
  };

  const closeActionMenu = () => {
    setActionMenu({ anchorEl: null, booking: null });
  };

  const handleMenuAction = (type) => {
    const booking = actionMenu.booking;
    closeActionMenu();
    if (!booking) return;
    if (type === 'view') openDetail(booking);
    else openAction(type, booking);
  };

  const openPaymentLinkDialog = (booking) => {
    const provider = defaultPaymentProvider(booking);
    setPaymentLinkDialog({ open: true, booking });
    setPaymentLinkProvider(provider);
    setPaymentLinkAmount(defaultPaymentAmount(booking, provider));
    setPaymentLinkNote('');
    setPaymentLinkError('');
    setLatestPaymentLink('');
  };

  const closePaymentLinkDialog = () => {
    if (paymentLinkSubmitting) return;
    setPaymentLinkDialog({ open: false, booking: null });
    setPaymentLinkError('');
    setPaymentLinkNote('');
    setLatestPaymentLink('');
  };

  const changePaymentLinkProvider = (provider) => {
    setPaymentLinkProvider(provider);
    setPaymentLinkAmount(defaultPaymentAmount(paymentLinkDialog.booking, provider));
  };

  const copyPaymentLink = async (link) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch (err) {
      const input = document.createElement('textarea');
      input.value = link;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
  };

  const submitPaymentLink = async () => {
    const bookingId = paymentLinkDialog.booking?._id;
    if (!bookingId) return;
    const amount = Number(paymentLinkAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentLinkError('Enter a valid amount greater than zero.');
      return;
    }

    try {
      setPaymentLinkSubmitting(true);
      setPaymentLinkError('');
      const result = await createAdminBookingPaymentLink(bookingId, {
        provider: paymentLinkProvider,
        amount,
        note: paymentLinkNote.trim(),
      });
      const link = result?.checkout?.actionUrl || result?.payment?.link || '';
      setLatestPaymentLink(link);
      setPaymentLinkDialog((current) => ({ ...current, booking: result?.booking || current.booking }));
      if (detailBooking && String(detailBooking._id) === String(bookingId)) {
        setDetailBooking(result?.booking || detailBooking);
      }
      await loadBookings();
    } catch (err) {
      setPaymentLinkError(err?.message || 'Failed to create payment link');
    } finally {
      setPaymentLinkSubmitting(false);
    }
  };

  const submitAction = async () => {
    if (!actionDialog.booking?._id) return;
    if (actionDialog.type === 'reject' && !actionNote.trim()) {
      setError('A rejection reason is required.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      if (actionDialog.type === 'confirm') {
        const result = await confirmAdminBooking(actionDialog.booking._id, actionNote.trim());
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else if (actionDialog.type === 'markPaid') {
        const result = await markAdminBookingPaid(actionDialog.booking._id, {
          note: actionNote.trim(),
          reference: actionReference.trim(),
          paymentMethod: actionMethod,
          evidenceSource: actionEvidenceSource,
          evidenceNote: actionNote.trim(),
        });
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else if (actionDialog.type === 'markPending') {
        const result = await markAdminBookingPaymentPending(actionDialog.booking._id, {
          note: actionNote.trim(),
        });
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else if (actionDialog.type === 'markFailed') {
        const result = await markAdminBookingPaymentFailed(actionDialog.booking._id, {
          reason: actionNote.trim(),
        });
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else {
        const result = await rejectAdminBooking(actionDialog.booking._id, actionNote.trim());
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      }
      closeAction();
      await loadBookings();
    } catch (err) {
      setError(err?.message || 'Failed to update booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardHeader title="Family bookings" subheader="Multi-student baskets, allocations, discounts and payment state." />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            <TableContainer className="admin-scroll-table" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Parent</TableCell><TableCell>Students</TableCell><TableCell>Allocations</TableCell><TableCell>Payment</TableCell><TableCell>Total</TableCell><TableCell>Updated</TableCell></TableRow></TableHead>
                <TableBody>
                  {bookingBasketsLoading ? <TableRow><TableCell colSpan={6}><LinearProgress /></TableCell></TableRow> : null}
                  {!bookingBasketsLoading && !bookingBaskets.length ? <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No multi-student booking baskets require review.</Typography></TableCell></TableRow> : null}
                  {bookingBaskets.map((basket) => {
                    const allocationCount = (basket.students || []).reduce((total, student) => total + (student.allocations || []).length, 0);
                    return <TableRow key={basket._id} hover onClick={() => openBasketDetail(basket)} sx={{ cursor: 'pointer' }}>
                      <TableCell><Typography variant="body2" fontWeight={700}>{formatPerson(basket.parent)}</Typography><Typography variant="caption" color="text.secondary">{basket.parent?.email || '-'}</Typography></TableCell>
                      <TableCell>{(basket.students || []).map((student) => student.student?.name || 'Student').join(', ') || '-'}</TableCell>
                      <TableCell>{allocationCount} · {basket.bookings?.filter((booking) => !booking.isDraft).length || 0} activated</TableCell>
                      <TableCell><Chip size="small" color={statusColor(basket.paymentStatus)} label={`${statusLabel(basket.paymentStatus)} · ${statusLabel(basket.bookingStatus)}`} /></TableCell>
                      <TableCell>{formatAdminMoney(basket.total, basket.currency)}</TableCell>
                      <TableCell>{formatDate(basket.updatedAt)}</TableCell>
                    </TableRow>;
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader title="Abandoned Guest Bookings" subheader="Guest parent details captured before learner setup or payment. Follow up only where consent permits." />
          <Divider />
          <CardContent sx={{ pb: 0 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                label="Search parent or learner"
                value={guestSearchInput}
                onChange={(event) => setGuestSearchInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') { setGuestQuery(guestSearchInput.trim()); changeGuestPage(1); } }}
                placeholder="Name, email or phone"
              />
              <Button variant="outlined" onClick={() => { setGuestQuery(guestSearchInput.trim()); changeGuestPage(1); }}>Search</Button>
              <FormControl size="small" sx={{ minWidth: { md: 145 } }}>
                <InputLabel id="guest-step-filter-label">Booking step</InputLabel>
                <Select labelId="guest-step-filter-label" label="Booking step" value={guestStep} onChange={(event) => { setGuestStep(event.target.value); changeGuestPage(1); }}>
                  <MenuItem value="">All steps</MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7].map((step) => <MenuItem key={step} value={String(step)}>Step {step}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { md: 180 } }}>
                <InputLabel id="guest-follow-up-filter-label">Follow-up</InputLabel>
                <Select labelId="guest-follow-up-filter-label" label="Follow-up" value={guestFollowUp} onChange={(event) => { setGuestFollowUp(event.target.value); changeGuestPage(1); }}>
                  <MenuItem value="">All follow-ups</MenuItem>
                  <MenuItem value="allowed">Allowed</MenuItem>
                  <MenuItem value="no_consent">No consent</MenuItem>
                  <MenuItem value="reminded">Reminder sent</MenuItem>
                  <MenuItem value="not_reminded">No reminder sent</MenuItem>
                  <MenuItem value="opted_out">Opted out</MenuItem>
                  <MenuItem value="resolved">Contacted</MenuItem>
                </Select>
              </FormControl>
              <Button onClick={() => { setGuestSearchInput(''); setGuestQuery(''); setGuestStep(''); setGuestFollowUp(''); changeGuestPage(1); }}>Clear</Button>
            </Stack>
            <TableContainer className="admin-scroll-table" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Parent</TableCell><TableCell>Progress</TableCell><TableCell>Last Activity</TableCell><TableCell>Consent</TableCell><TableCell>Follow-up</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                <TableBody>
                  {guestLeadLoading ? <TableRow><TableCell colSpan={6}><LinearProgress /></TableCell></TableRow> : null}
                  {!guestLeadLoading && !guestLeads.length ? <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No abandoned guest bookings match these filters.</Typography></TableCell></TableRow> : null}
                  {guestLeads.map((lead) => (
                    <TableRow key={lead._id} hover>
                      <TableCell><Typography variant="body2" fontWeight={700}>{formatPerson(lead.parent)}</Typography><Typography variant="caption" color="text.secondary">{lead.parent?.email || '-'} · {lead.parent?.phone || '-'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">Step {lead.currentStep || 1}</Typography><Typography variant="caption" color="text.secondary">{lead.progress?.packageName || lead.student?.curriculum || 'Parent details captured'}</Typography></TableCell>
                      <TableCell>{formatDate(lead.lastActivityAt)}</TableCell>
                      <TableCell><Chip size="small" color={lead.consent?.bookingFollowUp ? 'success' : 'default'} label={lead.consent?.bookingFollowUp ? 'Follow-up allowed' : 'No follow-up consent'} /></TableCell>
                      <TableCell>{lead.followUps?.lastReminderAt ? formatDate(lead.followUps.lastReminderAt) : 'Not sent'}</TableCell>
                      <TableCell align="right"><Stack direction="row" spacing={1} justifyContent="flex-end"><Button size="small" onClick={() => setSelectedGuestLead(lead)}>View</Button><Button size="small" disabled={!lead.booking?._id} onClick={() => openDetail(lead.booking)}>View Booking</Button><Button size="small" disabled={!lead.consent?.bookingFollowUp || Boolean(lead.optedOutAt) || Boolean(lead.resolvedAt)} onClick={() => handleGuestLeadAction(lead._id, 'resend')}>Resend</Button><Button size="small" onClick={() => handleGuestLeadAction(lead._id, 'resolve')}>Contacted</Button><Button size="small" color="warning" onClick={() => handleGuestLeadAction(lead._id, 'opt_out')}>Opt out</Button></Stack></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <AdminPagination
              page={guestPagination.page}
              pages={guestPagination.pages}
              total={guestPagination.total}
              pageSize={guestPageSize}
              label="abandoned guest bookings"
              disabled={guestLeadLoading}
              onPageChange={(nextPage) => changeGuestPage(nextPage)}
              onPageSizeChange={(nextSize) => changeGuestPage(1, nextSize)}
            />
          </CardContent>
        </Card>
        <Dialog open={Boolean(selectedGuestLead)} onClose={() => setSelectedGuestLead(null)} fullWidth maxWidth="sm">
          <DialogTitle>Guest booking progress</DialogTitle>
          <DialogContent>
            {selectedGuestLead && <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Typography><strong>Parent:</strong> {formatPerson(selectedGuestLead.parent)}</Typography>
              <Typography><strong>Learner:</strong> {formatPerson(selectedGuestLead.student)}</Typography>
              <Typography><strong>Last step:</strong> {selectedGuestLead.currentStep || 1} of 7</Typography>
              <Typography><strong>Last activity:</strong> {formatDate(selectedGuestLead.lastActivityAt)}</Typography>
              <Typography><strong>Package:</strong> {selectedGuestLead.progress?.packageName || 'Not selected'}</Typography>
              <Typography><strong>Curriculum:</strong> {selectedGuestLead.student?.curriculum || 'Not selected'}</Typography>
              <Typography><strong>Follow-up:</strong> {selectedGuestLead.optedOutAt ? 'Opted out' : selectedGuestLead.resolvedAt ? 'Contacted' : selectedGuestLead.consent?.bookingFollowUp ? 'Allowed' : 'No consent'}</Typography>
              <Typography><strong>Last reminder:</strong> {selectedGuestLead.followUps?.lastReminderAt ? formatDate(selectedGuestLead.followUps.lastReminderAt) : 'Not sent'}</Typography>
              <Typography><strong>Linked booking:</strong> {selectedGuestLead.booking?.bookingNumber || 'No booking created yet'}</Typography>
            </Stack>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedGuestLead(null)}>Close</Button>
            <Button variant="contained" disabled={!selectedGuestLead?.booking?._id} onClick={() => { const booking = selectedGuestLead.booking; setSelectedGuestLead(null); openDetail(booking); }}>View Booking</Button>
          </DialogActions>
        </Dialog>
        <Card>
          <CardHeader title="Manual Tutor Matching Queue" subheader={`${consultantPagination.total} request${consultantPagination.total === 1 ? '' : 's'} total`} />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            <TableContainer className="admin-scroll-table">
              <Table size="small">
                <TableHead><TableRow><TableCell>Received</TableCell><TableCell>Parent</TableCell><TableCell>Learner</TableCell><TableCell>Requirements</TableCell><TableCell>Availability</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {consultantLoading ? <TableRow><TableCell colSpan={6}><LinearProgress /></TableCell></TableRow> : null}
                  {!consultantLoading && !consultantRequests.length ? <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No consultant requests are waiting for manual matching.</Typography></TableCell></TableRow> : null}
                  {consultantRequests.map((request) => (
                    <TableRow key={request._id} hover>
                      <TableCell>{formatDate(request.createdAt)}</TableCell>
                      <TableCell><Typography variant="body2">{request.parent?.name || '-'}</Typography><Typography variant="caption" color="text.secondary">{request.parent?.email || '-'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{request.student?.name || '-'}</Typography><Typography variant="caption" color="text.secondary">{request.student?.curriculum || '-'} · {formatGradeLabel(request.student?.grade)}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{request.criteria?.subject || '-'}</Typography><Typography variant="caption" color="text.secondary">{request.student?.deliveryMode || '-'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{formatList(request.criteria?.selectedDays)}</Typography><Typography variant="caption" color="text.secondary">{formatList(request.criteria?.selectedTimeSlots)}{request.criteria?.timeZone ? ` · ${request.criteria.timeZone}` : ''}</Typography></TableCell>
                      <TableCell><Chip size="small" color={request.deliveryStatus === 'sent' ? 'warning' : 'error'} label={request.state === 'open' ? 'Open' : request.state} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <AdminPagination
              page={consultantPagination.page}
              pages={consultantPagination.pages}
              total={consultantPagination.total}
              pageSize={consultantPageSize}
              label="manual matching requests"
              disabled={consultantLoading}
              onPageChange={(nextPage) => changeConsultantPage(nextPage)}
              onPageSizeChange={(nextSize) => changeConsultantPage(1, nextSize)}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Stack className={`booking-filter-toolbar${filtersExpanded ? ' is-expanded' : ''}`} direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
              <FormControl fullWidth sx={{ minWidth: { md: 220 }, flex: { md: '1 1 220px' } }}>
                <InputLabel id="booking-status-label">Booking Status</InputLabel>
                <Select
                  labelId="booking-status-label"
                  value={bookingStatus}
                  label="Booking Status"
                  onChange={(e) => { setBookingStatus(e.target.value); changeBookingPage(1); }}
                >
                  {BOOKING_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ minWidth: { md: 220 }, flex: { md: '1 1 220px' } }}>
                <InputLabel id="payment-status-label">Payment Status</InputLabel>
                <Select
                  labelId="payment-status-label"
                  value={paymentStatus}
                  label="Payment Status"
                  onChange={(e) => { setPaymentStatus(e.target.value); changeBookingPage(1); }}
                >
                  {PAYMENT_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                sx={{ flex: { md: '1 1 220px' } }}
                label="Search"
                value={query}
                onChange={(e) => { setQuery(e.target.value); changeBookingPage(1); }}
                placeholder="Booking number, subject, or area"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadBookings();
                }}
              />

              <Button className="booking-more-filters" variant="outlined" onClick={() => setFiltersExpanded((value) => !value)}>
                {filtersExpanded ? 'Hide filters' : 'More filters'}
              </Button>

              <FormControl className="booking-advanced-filter" fullWidth sx={{ minWidth: { md: 180 }, flex: { md: '1 1 180px' } }}>
                <InputLabel id="delivery-mode-label">Delivery Mode</InputLabel>
                <Select
                  labelId="delivery-mode-label"
                  value={deliveryMode}
                  label="Delivery Mode"
                  onChange={(e) => { setDeliveryMode(e.target.value); changeBookingPage(1); }}
                >
                  {DELIVERY_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl className="booking-advanced-filter" fullWidth sx={{ minWidth: { md: 180 }, flex: { md: '1 1 180px' } }}>
                <InputLabel id="curriculum-label">Curriculum</InputLabel>
                <Select
                  labelId="curriculum-label"
                  value={curriculum}
                  label="Curriculum"
                  onChange={(e) => { setCurriculum(e.target.value); changeBookingPage(1); }}
                >
                  {CURRICULUM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl className="booking-advanced-filter" fullWidth sx={{ minWidth: { md: 180 }, flex: { md: '1 1 180px' } }}>
                <InputLabel id="province-label">Province</InputLabel>
                <Select
                  labelId="province-label"
                  value={province}
                  label="Province"
                  onChange={(e) => { setProvince(e.target.value); changeBookingPage(1); }}
                >
                  {PROVINCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button className="booking-refresh" variant="outlined" onClick={loadBookings} disabled={loading} sx={{ flex: { md: '0 0 auto' } }}>
                Refresh
              </Button>
            </Stack>

            {loading && <LinearProgress sx={{ mt: 2 }} />}
            {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 3 }}>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Awaiting Review</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.awaiting}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Paid</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.paid}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Confirmed</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.confirmed}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Rejected</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.rejected}</Typography>
                </CardContent>
              </Card>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Session Link Failures</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.sessionLinkFailures ?? 0}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Awaiting > 24h</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.pendingTooLong ?? 0}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Tutor Rejected</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.tutorRejected ?? 0}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Failed Payments</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.failedPayments ?? 0}</Typography>
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Tutor Requests & Bookings"
            subheader={`${bookingPagination.total} record${bookingPagination.total === 1 ? '' : 's'} total`}
          />
          <Divider />
          <CardContent sx={{ p: 0, display: { xs: 'none', lg: 'block' } }}>
            <TableContainer className="admin-scroll-table" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Booking</TableCell>
                    <TableCell>Parent</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Tutor</TableCell>
                    <TableCell>Lesson</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Tutor Decision</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No bookings match the current filters.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {bookings.map((booking) => {
                    const tutorAssignments = bookingTutorAssignments(booking);
                    return (
                      <TableRow key={booking._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>{adminReferenceLabel(booking, booking.bookingNumber)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.subject} · {booking.curriculum} · {formatGradeLabel(booking.student?.grade ?? booking.grade)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatPerson(booking.parent)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.parent?.email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatPerson(booking.student)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(booking.student?.profileReadiness?.readyForMatching ? 'Ready to match' : 'Profile incomplete')}
                            {' · '}
                            {booking.packageName || booking.student?.packageName || 'No package'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {booking.student?.province || booking.province || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {tutorAssignments.length ? tutorAssignments.map((assignment, index) => (
                            <Typography key={`${assignment.subject || 'tutor'}-${index}`} variant="body2">
                              {assignment.subject ? `${assignment.subject}: ` : ''}{formatPerson(assignment.tutor)}
                            </Typography>
                          )) : <Typography variant="body2">-</Typography>}
                          <Typography variant="caption" color="text.secondary">
                            {tutorAssignments.length
                              ? (booking.subjectAllocations?.length ? 'Tutor assigned per subject' : 'Tutor chosen by parent')
                              : 'No tutor selected yet'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDeliveryMode(booking.deliveryMode)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.hoursRequested} hour{Number(booking.hoursRequested) === 1 ? '' : 's'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel(booking.bookingStatus)} color={statusColor(booking.bookingStatus)} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel(booking.paymentStatus)} color={statusColor(booking.paymentStatus)} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel(booking.tutorDecisionStatus || 'pending')} color={statusColor(booking.tutorDecisionStatus)} />
                        </TableCell>
                        <TableCell>{formatDate(booking.createdAt)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={(event) => openActionMenu(event, booking)}>
                            Actions
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
          <CardContent sx={{ display: { xs: 'block', lg: 'none' }, p: 1.5 }}>
            <Stack spacing={1.25}>
              {!loading && bookings.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No bookings match the current filters.</Typography>
                </Box>
              ) : null}
              {bookings.map((booking) => {
                const tutorAssignments = bookingTutorAssignments(booking);
                const canReview =
                  ['pending_admin_confirmation', 'tutor_response_overdue'].includes(booking.bookingStatus) &&
                  ['paid_mock', 'paid_manual', 'paid_gateway'].includes(booking.paymentStatus) &&
                  booking.tutorDecisionStatus !== 'rejected';
                const canMarkPaid =
                  ['pending', 'submitted_manual', 'failed'].includes(booking.paymentStatus) &&
                  !['confirmed', 'rejected'].includes(booking.bookingStatus);
                return (
                  <Card key={booking._id} variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={800} noWrap>{adminReferenceLabel(booking, booking.bookingNumber)}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                              {booking.subject} · {booking.curriculum} · {formatGradeLabel(booking.student?.grade ?? booking.grade)}
                            </Typography>
                          </Box>
                          <Button size="small" variant="outlined" onClick={(event) => openActionMenu(event, booking)}>Actions</Button>
                        </Stack>
                        <Typography variant="body2"><strong>Parent:</strong> {formatPerson(booking.parent)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{booking.parent?.email || '-'}</Typography>
                        <Typography variant="body2"><strong>Student:</strong> {formatPerson(booking.student)}</Typography>
                        <Box>
                          <Typography variant="body2"><strong>Tutor{tutorAssignments.length === 1 ? '' : 's'}:</strong></Typography>
                          {tutorAssignments.length ? tutorAssignments.map((assignment, index) => (
                            <Typography key={`${assignment.subject || 'tutor'}-${index}`} variant="body2" sx={{ pl: 1 }}>
                              {assignment.subject ? `${assignment.subject}: ` : ''}{formatPerson(assignment.tutor)}
                            </Typography>
                          )) : <Typography variant="body2" sx={{ pl: 1 }}>Not assigned</Typography>}
                        </Box>
                        <Typography variant="caption" color="text.secondary">{formatDeliveryMode(booking.deliveryMode)} · {booking.hoursRequested} hour{Number(booking.hoursRequested) === 1 ? '' : 's'} · {booking.student?.province || booking.province || '-'}</Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={statusLabel(booking.bookingStatus)} color={statusColor(booking.bookingStatus)} />
                          <Chip size="small" label={statusLabel(booking.paymentStatus)} color={statusColor(booking.paymentStatus)} />
                          {canReview || canMarkPaid ? <Chip size="small" variant="outlined" label="Action required" /> : null}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </CardContent>
          <AdminPagination
            page={bookingPagination.page}
            pages={bookingPagination.pages}
            total={bookingPagination.total}
            pageSize={bookingPageSize}
            label="bookings"
            disabled={loading}
            onPageChange={(nextPage) => changeBookingPage(nextPage)}
            onPageSizeChange={(nextSize) => changeBookingPage(1, nextSize)}
          />
        </Card>
      </Stack>

      <Menu anchorEl={actionMenu.anchorEl} open={Boolean(actionMenu.anchorEl)} onClose={closeActionMenu}>
        <MenuItem onClick={() => handleMenuAction('view')}>View booking</MenuItem>
        <MenuItem disabled={!actionMenu.booking || !['pending', 'submitted_manual', 'failed'].includes(actionMenu.booking.paymentStatus) || ['confirmed', 'rejected'].includes(actionMenu.booking.bookingStatus)} onClick={() => handleMenuAction('markPaid')}>Mark paid</MenuItem>
        <MenuItem disabled={!actionMenu.booking || !['pending_admin_confirmation', 'tutor_response_overdue'].includes(actionMenu.booking.bookingStatus) || !['paid_mock', 'paid_manual', 'paid_gateway'].includes(actionMenu.booking.paymentStatus) || actionMenu.booking.tutorDecisionStatus === 'rejected'} onClick={() => handleMenuAction('confirm')}>Confirm booking</MenuItem>
        <MenuItem disabled={!actionMenu.booking || !['pending_admin_confirmation', 'tutor_response_overdue'].includes(actionMenu.booking.bookingStatus) || !['paid_mock', 'paid_manual', 'paid_gateway'].includes(actionMenu.booking.paymentStatus) || actionMenu.booking.tutorDecisionStatus === 'rejected'} onClick={() => handleMenuAction('reject')}>Reject booking</MenuItem>
      </Menu>

      <Dialog open={actionDialog.open} onClose={closeAction} fullWidth maxWidth="sm">
        <DialogTitle>
          {actionDialog.type === 'confirm' ? 'Confirm Tutor Request' : null}
          {actionDialog.type === 'reject' ? 'Reject Tutor Request' : null}
          {actionDialog.type === 'markPaid' ? 'Mark Payment As Paid' : null}
          {actionDialog.type === 'markPending' ? 'Return Payment To Pending' : null}
          {actionDialog.type === 'markFailed' ? 'Mark Payment As Failed' : null}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {actionDialog.booking?.bookingNumber || '-'} · {actionDialog.booking?.subject || '-'} · {formatPerson(actionDialog.booking?.selectedTutor)}
            </Typography>
            {actionDialog.type === 'markPaid' ? (
              <TextField
                fullWidth
                label="Payment Reference"
                value={actionReference}
                onChange={(e) => setActionReference(e.target.value)}
                placeholder="Bank or transfer reference"
              />
            ) : null}
            {actionDialog.type === 'markPaid' ? (
              <FormControl fullWidth>
                <InputLabel id="action-method-label">Payment Method</InputLabel>
                <Select
                  labelId="action-method-label"
                  value={actionMethod}
                  label="Payment Method"
                  onChange={(e) => setActionMethod(e.target.value)}
                >
                  <MenuItem value="manual_transfer">Manual Transfer</MenuItem>
                  <MenuItem value="eft">EFT</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card_manual">Card Manual</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            {actionDialog.type === 'markPaid' ? (
              <FormControl fullWidth>
                <InputLabel id="action-evidence-label">Evidence Source</InputLabel>
                <Select
                  labelId="action-evidence-label"
                  value={actionEvidenceSource}
                  label="Evidence Source"
                  onChange={(e) => setActionEvidenceSource(e.target.value)}
                >
                  <MenuItem value="bank_reference">Bank Reference</MenuItem>
                  <MenuItem value="proof_of_payment">Proof Of Payment</MenuItem>
                  <MenuItem value="cash_confirmation">Cash Confirmation</MenuItem>
                  <MenuItem value="card_confirmation">Card Confirmation</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            <TextField
              multiline
              minRows={3}
              fullWidth
              label={
                actionDialog.type === 'confirm'
                  ? 'Confirmation Note'
                  : actionDialog.type === 'markPaid'
                    ? 'Payment Note'
                    : actionDialog.type === 'markPending'
                      ? 'Pending Note'
                      : actionDialog.type === 'markFailed'
                        ? 'Failure Reason'
                        : 'Rejection Reason'
              }
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              placeholder={
                actionDialog.type === 'confirm'
                  ? 'Approved by admin and ready for LMS scheduling'
                  : actionDialog.type === 'markPaid'
                    ? 'Payment received and verified by admin'
                    : actionDialog.type === 'markPending'
                      ? 'State why payment is back in pending review'
                      : actionDialog.type === 'markFailed'
                        ? 'State why payment failed verification'
                        : 'State why the tutor request is being rejected'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color={
              actionDialog.type === 'confirm' || actionDialog.type === 'markPaid'
                ? 'success'
                : actionDialog.type === 'markPending'
                  ? 'warning'
                  : 'error'
            }
            onClick={submitAction}
            disabled={submitting}
          >
            {actionDialog.type === 'confirm' ? 'Confirm Request' : null}
            {actionDialog.type === 'reject' ? 'Reject Request' : null}
            {actionDialog.type === 'markPaid' ? 'Mark Paid' : null}
            {actionDialog.type === 'markPending' ? 'Mark Pending' : null}
            {actionDialog.type === 'markFailed' ? 'Mark Failed' : null}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentLinkDialog.open} onClose={closePaymentLinkDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Payment Link</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {paymentLinkDialog.booking?.bookingNumber || '-'} · {paymentLinkDialog.booking?.subject || '-'}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="payment-link-provider-label">Provider</InputLabel>
              <Select
                labelId="payment-link-provider-label"
                value={paymentLinkProvider}
                label="Provider"
                onChange={(e) => changePaymentLinkProvider(e.target.value)}
              >
                <MenuItem value="yoco">Yoco - Local ZAR</MenuItem>
                <MenuItem value="paypal">PayPal - International USD</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={`Amount (${paymentLinkProvider === 'paypal' ? 'USD' : 'ZAR'})`}
              type="number"
              value={paymentLinkAmount}
              onChange={(e) => setPaymentLinkAmount(e.target.value)}
              inputProps={{ min: '0.01', step: '0.01' }}
              helperText={
                paymentLinkProvider === 'paypal'
                  ? 'PayPal links are created in USD.'
                  : 'Yoco links are created in ZAR.'
              }
            />
            <TextField
              multiline
              minRows={3}
              fullWidth
              label="Admin Note"
              value={paymentLinkNote}
              onChange={(e) => setPaymentLinkNote(e.target.value)}
              placeholder="Optional note for this payment link"
            />
            {paymentLinkError ? <Alert severity="error">{paymentLinkError}</Alert> : null}
            {latestPaymentLink ? (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={800}>Payment link created</Typography>
                <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                  {latestPaymentLink}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => copyPaymentLink(latestPaymentLink)}
                >
                  Copy Link
                </Button>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentLinkDialog} disabled={paymentLinkSubmitting}>
            {latestPaymentLink ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={submitPaymentLink}
            disabled={paymentLinkSubmitting || Boolean(latestPaymentLink)}
          >
            {paymentLinkSubmitting ? 'Creating...' : 'Create Link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={detailOpen} onClose={closeDetail}>
        <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>Booking Detail</Typography>
            {detailLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : detailBooking ? (
              <>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">Booking Number</Typography>
                      <Typography variant="body1" fontWeight={700}>{adminReferenceLabel(detailBooking, detailBooking.bookingNumber)}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip size="small" label={statusLabel(detailBooking.bookingStatus)} color={statusColor(detailBooking.bookingStatus)} />
                        <Chip size="small" label={statusLabel(detailBooking.paymentStatus)} color={statusColor(detailBooking.paymentStatus)} />
                        <Chip size="small" label={`Tutor: ${statusLabel(detailBooking.tutorDecisionStatus || 'pending')}`} color={statusColor(detailBooking.tutorDecisionStatus)} />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Parent</Typography>
                      <Typography variant="body2">{formatPerson(detailBooking.parent)}</Typography>
                      <Typography variant="caption" color="text.secondary">{detailBooking.parent?.email || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{detailBooking.parent?.phone || '-'}</Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Student Profile</Typography>
                      <Typography variant="body2">{formatPerson(detailBooking.student)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatGradeLabel(detailBooking.student?.grade ?? detailBooking.grade)} · {detailBooking.curriculum}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(detailBooking.student?.schoolName || 'School not provided')}
                        {detailBooking.student?.curriculumLevel ? ` · ${detailBooking.student.curriculumLevel}` : ''}
                        {detailBooking.student?.curriculumSubLevel ? ` · ${detailBooking.student.curriculumSubLevel}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Package: {detailBooking.student?.packageName || detailBooking.packageName || 'Not assigned'}
                        {detailBooking.student?.packageSessionsRemaining != null
                          ? ` · Sessions left ${detailBooking.student.packageSessionsRemaining}`
                          : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Matching readiness: {detailBooking.student?.profileReadiness?.completionPercent ?? 0}% complete
                        {detailBooking.student?.profileReadiness?.readyForMatching ? ' · Ready' : ' · Incomplete'}
                      </Typography>
                      {!detailBooking.student?.profileReadiness?.readyForMatching && (detailBooking.student?.profileReadiness?.missingFields || []).length ? (
                        <Typography variant="caption" color="warning.main">
                          Missing: {detailBooking.student.profileReadiness.missingFields.join(', ')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Matching Profile</Typography>
                      <Typography variant="body2">
                        Subject focus: {detailBooking.subject}
                        {detailBooking.student?.subjects?.length ? ` · ${detailBooking.student.subjects.join(', ')}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDeliveryMode(detailBooking.student?.deliveryMode || detailBooking.deliveryMode)}
                        {' · '}
                        {detailBooking.student?.preferredLessonDuration || detailBooking.hoursRequested} hour{Number(detailBooking.student?.preferredLessonDuration || detailBooking.hoursRequested) === 1 ? '' : 's'}
                        {' · '}
                        {detailBooking.student?.preferredLessonFrequency || 'Frequency not set'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Language: {detailBooking.student?.preferredLanguage || 'Not set'}
                        {' · '}
                        Learning style: {detailBooking.student?.learningStyle || 'Not set'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tutor preference: {detailBooking.student?.preferredTutorGender || 'No gender preference'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Teaching styles: {formatList(detailBooking.student?.preferredTeachingStyles)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Learning support: {formatList(detailBooking.student?.learningSupportNeeds)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Special needs: {formatList(detailBooking.student?.specialNeedsSupport)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Availability: {formatAvailability(detailBooking.student?.availabilitySchedule)}
                      </Typography>
                      {detailBooking.student?.academicGoals ? (
                        <Typography variant="caption" color="text.secondary">
                          Academic goals: {detailBooking.student.academicGoals}
                        </Typography>
                      ) : null}
                      {detailBooking.student?.notes ? (
                        <Typography variant="caption" color="text.secondary">
                          Notes: {detailBooking.student.notes}
                        </Typography>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Tutor</Typography>
                      <Typography variant="body2">{formatPerson(detailBooking.selectedTutor)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {detailBooking.selectedTutor?.email || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Decision: {detailBooking.tutorDecisionStatus || 'pending'} · {formatDate(detailBooking.tutorDecisionAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Note: {detailBooking.tutorDecisionNote || '-'}
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 0.5 }}>
                        <Button size="small" variant="outlined" disabled={!tutorIdOf(detailBooking.selectedTutor)} onClick={() => openTutorProfile(detailBooking.selectedTutor, detailBooking.subject)}>View Tutor</Button>
                        <Button size="small" variant="contained" disabled={!tutorIdOf(detailBooking.selectedTutor)} onClick={() => messageTutor(detailBooking.selectedTutor, detailBooking)}>Message Tutor</Button>
                      </Stack>
                      {!tutorIdOf(detailBooking.selectedTutor) ? <Typography variant="caption" color="text.secondary">No tutor is assigned to this booking.</Typography> : null}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Lesson</Typography>
                      <Typography variant="body2">{detailBooking.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDeliveryMode(detailBooking.deliveryMode)} · {detailBooking.hoursRequested} hour{Number(detailBooking.hoursRequested) === 1 ? '' : 's'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {detailBooking.student?.addressLine1 || detailBooking.addressLine1 || '-'}, {detailBooking.student?.suburb || detailBooking.suburb || '-'}, {detailBooking.student?.town || detailBooking.town || '-'}, {detailBooking.student?.province || detailBooking.province || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requested start: {formatDate(detailBooking.requestedStartAt)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {(detailBooking.subjectAllocations || []).length ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" fontWeight={700}>Subject Distribution</Typography>
                        {detailBooking.subjectAllocations.map((allocation) => (
                          <Box key={allocation._id || allocation.subject} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1} flexWrap="wrap">
                              <Typography variant="body2" fontWeight={800}>{allocation.subject}</Typography>
                              <Chip size="small" label={statusLabel(allocation.tutorDecisionStatus || allocation.status)} color={statusColor(allocation.tutorDecisionStatus || allocation.status)} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Sessions: {allocation.sessionCount || '-'} · Duration: {allocation.hoursPerSession || detailBooking.hoursRequested} hour{Number(allocation.hoursPerSession || detailBooking.hoursRequested) === 1 ? '' : 's'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Tutor: {allocation.selectedTutor?.name || formatPerson(allocation.selectedTutorId) || 'Not assigned'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Requested start: {formatDate(allocation.requestedStartAt)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Session link: {statusLabel(allocation.sessionLinkStatus || 'not_created')}
                            </Typography>
                            {allocation.tutorDecisionNote ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Tutor note: {allocation.tutorDecisionNote}
                              </Typography>
                            ) : null}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                              <Button size="small" variant="outlined" disabled={!tutorIdOf(allocation.selectedTutor || allocation.selectedTutorId)} onClick={() => openTutorProfile(allocation.selectedTutor || allocation.selectedTutorId, allocation.subject)}>View Tutor</Button>
                              <Button size="small" variant="contained" disabled={!tutorIdOf(allocation.selectedTutor || allocation.selectedTutorId)} onClick={() => messageTutor(allocation.selectedTutor || allocation.selectedTutorId, detailBooking, allocation.subject)}>Message Tutor</Button>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Finance View</Typography>
                      <Typography variant="body2">Client rate / hour: {formatAdminMoney(detailBooking.pricingSnapshot?.clientRate, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Tutor remuneration / hour: {formatAdminMoney(detailBooking.pricingSnapshot?.tutorRate, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Client total: {formatAdminMoney(detailBooking.pricingSnapshot?.totalClientAmount, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Tutor remuneration total: {formatAdminMoney(detailBooking.pricingSnapshot?.totalTutorAmount, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Commission / margin: {formatAdminMoney(detailBooking.pricingSnapshot?.commissionAmount, detailBooking.pricingSnapshot?.currency)}</Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Admin Decision</Typography>
                      <Typography variant="body2">{detailBooking.adminDecisionNote || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Decision at: {formatDate(detailBooking.adminDecisionAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Decision by: {formatPerson(detailBooking.adminDecisionBy)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>Payment History</Typography>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'grey.50' }}>
                        <Stack spacing={1}>
                          <Typography variant="body2" fontWeight={800}>Create Payment Link</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Generate a Yoco ZAR link for local bookings or a PayPal USD link for international bookings.
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => openPaymentLinkDialog(detailBooking)}
                            disabled={!canCreatePaymentLink(detailBooking)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Create Payment Link
                          </Button>
                          {!canCreatePaymentLink(detailBooking) ? (
                            <Typography variant="caption" color="text.secondary">
                              Payment links are disabled for paid, confirmed, rejected, or cancelled bookings.
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>
                      {(detailBooking.payments || []).length ? (
                        detailBooking.payments.map((payment) => (
                          <Box key={payment._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="body2" fontWeight={700}>{payment.reference}</Typography>
                              <Chip size="small" label={statusLabel(payment.status)} color={statusColor(payment.status)} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {payment.currency} {payment.amount} · {payment.provider}
                            </Typography>
                            {payment.providerReference ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Provider reference: {payment.providerReference}
                              </Typography>
                            ) : null}
                            {payment.paymentMethod ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Method: {payment.paymentMethod}
                              </Typography>
                            ) : null}
                            <Typography variant="caption" color="text.secondary" display="block">
                              Requested: {formatDate(payment.requestedAt)}
                            </Typography>
                            {payment.submittedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Submitted: {formatDate(payment.submittedAt)}
                              </Typography>
                            ) : null}
                            {payment.confirmedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Confirmed: {formatDate(payment.confirmedAt)}
                              </Typography>
                            ) : null}
                            {payment.failedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Failed: {formatDate(payment.failedAt)}
                              </Typography>
                            ) : null}
                            {payment.statusReason ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Note: {payment.statusReason}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceSource ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Evidence source: {statusLabel(payment.evidence.evidenceSource)}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceCheckedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Evidence checked: {formatDate(payment.evidence.evidenceCheckedAt)}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceCheckedByEmail ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Checked by: {payment.evidence.evidenceCheckedByEmail}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceNote ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Evidence note: {payment.evidence.evidenceNote}
                              </Typography>
                            ) : null}
                            {paymentLinkForPayment(payment) ? (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                                  Payment link: {paymentLinkForPayment(payment)}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ mt: 1 }}
                                  onClick={() => copyPaymentLink(paymentLinkForPayment(payment))}
                                >
                                  Copy Link
                                </Button>
                              </Box>
                            ) : null}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No payment history yet.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => openAction('markPaid', detailBooking)}
                        disabled={detailBooking.bookingStatus === 'confirmed' || detailBooking.bookingStatus === 'rejected'}
                      >
                        Mark Paid
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => openAction('markPending', detailBooking)}
                        disabled={detailBooking.bookingStatus === 'confirmed'}
                      >
                        Mark Pending
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => openAction('markFailed', detailBooking)}
                        disabled={detailBooking.bookingStatus === 'confirmed'}
                      >
                        Mark Failed
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>Tutor Shortlist</Typography>
                      {(detailBooking.assignmentHistory || []).length ? (
                        detailBooking.assignmentHistory.map((entry) => (
                          <Box key={entry._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="body2" fontWeight={700}>{formatPerson(entry.tutor)}</Typography>
                              <Chip size="small" label={entry.state} color={entry.state === 'selected' ? 'success' : 'default'} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Rank {entry.rank || '-'} · Match score {entry.matchScore ?? '-'}
                            </Typography>
                            {entry.rationale ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {entry.rationale}
                              </Typography>
                            ) : null}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                              <Button size="small" variant="outlined" disabled={!tutorIdOf(entry.tutor)} onClick={() => openTutorProfile(entry.tutor, detailBooking.subject)}>View Tutor</Button>
                              <Button size="small" variant="contained" disabled={!tutorIdOf(entry.tutor)} onClick={() => messageTutor(entry.tutor, detailBooking)}>Message Tutor</Button>
                            </Stack>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No shortlist history recorded.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>Timeline</Typography>
                      {(detailBooking.timeline || []).length ? (
                        detailBooking.timeline.map((entry, index) => (
                          <Box key={`${entry.type}-${entry.at}-${index}`} sx={{ borderLeft: '2px solid', borderColor: timelineTone(entry.tone), pl: 2, py: 0.5 }}>
                            <Typography variant="body2" fontWeight={700}>{timelineLabel(entry)}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatDate(entry.at)}
                            </Typography>
                            {entry.meta ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {entry.meta}
                              </Typography>
                            ) : null}
                            {entry.note ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {entry.note}
                              </Typography>
                            ) : null}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No timeline entries recorded.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </>
            ) : null}
          </Stack>
        </Box>
      </Drawer>

      <Dialog
        open={Boolean(basketDetail)}
        onClose={() => setBasketDetail(null)}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>Family booking details</DialogTitle>
        <DialogContent dividers>
          {basketDetailLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
          {basketDetail ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={800}>{formatPerson(basketDetail.parent)}</Typography>
                <Typography variant="body2" color="text.secondary">{basketDetail.parent?.email || '-'} · {basketDetail.parent?.phone || '-'}</Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                <Chip label={`Payment: ${statusLabel(basketDetail.paymentStatus)}`} color={statusColor(basketDetail.paymentStatus)} />
                <Chip label={`Booking: ${statusLabel(basketDetail.bookingStatus)}`} />
                <Chip label={`Total: ${formatAdminMoney(basketDetail.total, basketDetail.currency)}`} color="success" />
                <Chip label={`Sibling discount: ${formatAdminMoney(basketDetail.siblingDiscountAmount, basketDetail.currency)}`} />
              </Stack>
              {(basketDetail.students || []).map((student) => (
                <Card key={student._id} variant="outlined">
                  <CardHeader title={student.student?.name || 'Student'} subheader={`${student.packageName || 'Package not set'} · ${student.packageSessions || 0} sessions · ${formatAdminMoney(student.total, basketDetail.currency)}`} />
                  <CardContent sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {(student.allocations || []).map((allocation) => (
                        <Box key={allocation._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}>
                          <Typography variant="body2" fontWeight={700}>{allocation.subject} · {allocation.curriculum} · {formatGradeLabel(allocation.grade)}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{formatDeliveryMode(allocation.deliveryMode)} · {allocation.sessionCount} sessions · Tutor {allocation.tutor ? formatPerson(allocation.tutor) : 'not selected'}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{allocation.requestedStartAt ? formatDate(allocation.requestedStartAt) : 'Time not selected'}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {basketDetail.failureReason ? <Alert severity="error">{basketDetail.failureReason}</Alert> : null}
              <Typography variant="subtitle2" fontWeight={800}>Child booking records</Typography>
              {(basketDetail.bookings || []).map((booking) => <Typography key={booking._id} variant="body2">{booking.bookingNumber || booking._id} · {statusLabel(booking.paymentStatus)} · {statusLabel(booking.bookingStatus)}{booking.isDraft ? ' · draft' : ''}</Typography>)}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions><Button onClick={() => setBasketDetail(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog
        open={tutorProfileDialog.open}
        onClose={() => setTutorProfileDialog({ open: false, tutor: null, context: '' })}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{ sx: { m: { xs: 1.5, sm: 3 }, width: { xs: 'calc(100% - 24px)', sm: '100%' }, maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 64px)' } } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Privacy-safe Tutor Profile</DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto', px: { xs: 2, sm: 3 } }}>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <Typography variant="h6" fontWeight={800}>{formatPerson(tutorProfileDialog.tutor)}</Typography>
            {tutorProfileDialog.context ? <Typography variant="body2" color="text.secondary">Booking subject: {tutorProfileDialog.context}</Typography> : null}
            <Typography variant="body2">Qualification: {[tutorProfileDialog.tutor?.degreeType, tutorProfileDialog.tutor?.degreeSpecialization].filter(Boolean).join(' - ') || 'Not listed'}</Typography>
            <Typography variant="body2">Curricula: {formatList(tutorProfileDialog.tutor?.curricula)}</Typography>
            <Typography variant="body2">Grades: {formatList(tutorProfileDialog.tutor?.gradeLevels)}</Typography>
            <Typography variant="body2">Subjects: {formatList(tutorProfileDialog.tutor?.subjectFocus)}</Typography>
            <Typography variant="body2">Teaching styles: {formatList(tutorProfileDialog.tutor?.teachingStyles)}</Typography>
            {tutorProfileDialog.tutor?.bio ? <Typography variant="body2">{tutorProfileDialog.tutor.bio}</Typography> : null}
            <Alert severity="info">Private contact, identity, banking, address, and application-document fields are intentionally excluded.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1, px: { xs: 2, sm: 3 }, '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } } }}>
          <Button fullWidth onClick={() => setTutorProfileDialog({ open: false, tutor: null, context: '' })}>Close</Button>
          <Button fullWidth variant="contained" disabled={!tutorIdOf(tutorProfileDialog.tutor)} onClick={() => messageTutor(tutorProfileDialog.tutor, detailBooking, tutorProfileDialog.context)}>Message Tutor</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Bookings;
